import { motion } from "framer-motion";

import EtIlluminationIcon1SVGComp from '@/assets/img/icons/svgComp/EtIlluminationIcon1SVGComp'
import EtInductionIcon1SVGComp from '@/assets/img/icons/svgComp/EtInductionIcon1SVGComp'
import EtImprintIcon2SVGComp from '@/assets/img/icons/svgComp/EtImprintIcon2SVGComp'

// const steps = [
//   { title: "Induce", desc: "Understanding your narrative, identity & intent." },
//   { title: "Imprint", desc: "Structuring missions, goals, habits & focus." },
//   { title: "Illuminate", desc: "Driving execution & progress visibility." },
// ];

// Icons mapped to each step in order
const iconMap = {
  Induce: EtInductionIcon1SVGComp,
  Imprint: EtImprintIcon2SVGComp,
  Illuminate: EtIlluminationIcon1SVGComp,
};

// const steps = [
//   {
//     title: "Induce",
//     desc: "Understanding your narrative, identity, habits, behaviors, and innate drivers. Induce reveals the inner patterns that shape your leadership.",
//   },
//   {
//     title: "Imprint",
//     desc: "Structuring missions, goals, priorities, habits, and rhythms. Imprint transforms raw insight into a clear, purpose-native operating model.",
//   },
//   {
//     title: "Illuminate",
//     desc: "Driving execution and progress visibility — daily consistency, aligned actions, and intelligent nudges that help you stay on course.",
//   },
// ];

// 23 Nov 2025
const steps = [
  {
    title: "Induce",
    lines: [
      <>Surfaces the <span className="text-orange-600">inner patterns</span> that shape how you think and lead.</>,
      <>Captures experiential learning that usually gets lost.</>,
      <>Helps you understand your <span className="text-orange-600">drivers, habits, and identity</span>.</>,
    ],
  },
  {
    title: "Imprint",
    lines: [
      <>Transforms raw insight into a clear <span className="text-orange-600">operating rhythm</span>.</>,
      <>Structures missions, goals, priorities, and habits into a daily rhythm.</>,
      <>Creates a living imprint of who you are and how you evolve.</>,
    ],
  },
  {
    title: "Illuminate",
    lines: [
      <>Builds visibility into your daily contributions and progress.</>,
      <>Highlights the <span className="text-orange-600">impact</span> you create each week.</>,
      <>Gives you intelligent nudges that help you move with consistency.</>,
    ],
  },
];




export default function EtTrinityFramework() {

  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose — Et Trinity Framework
  // Updated - 22 Nov 2025 - Cinematic Crescendo: Induce → Imprint → Illuminate
  // Updated - 23 Nov 2025 - Copy Refinement


  return (

    <section className="relative overflow-hidden py-36 bg-black text-white">

      {/* --- 1. Deep Gradient Background --- */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-900/10 to-black"></div>

      {/* --- 2. Central Axis Glow --- */}
      <div
        className="
          absolute left-1/2 top-0 -translate-x-1/2
          w-[2px] h-full
          bg-gradient-to-b from-orange-600/40 via-orange-500/15 to-transparent
          blur-[2px]
        "
      ></div>

      {/* --- 3. Tri-Arc Rings (Signature Trinity Motif) --- */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="absolute w-[900px] h-[900px] rounded-full border border-orange-500/10 blur-[2px] animate-trinity-ring-1"></div>
        <div className="absolute w-[700px] h-[700px] rounded-full border border-orange-400/10 blur-[2px] animate-trinity-ring-2"></div>
        <div className="absolute w-[500px] h-[500px] rounded-full border border-orange-300/10 blur-[2px] animate-trinity-ring-3"></div>
      </div>

      {/* --- 4. Floating Particles (intense but clean) --- */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(90)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[3px] h-[3px] bg-orange-400/30 rounded-full animate-framework-spark"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          ></div>
        ))}
      </div>

      {/* --- 5. Section Heading --- */}
      <motion.h2
        className="relative z-20 text-center text-4xl md:text-5xl font-bold text-orange-400 mb-16 drop-shadow-[0_0_20px_rgba(255,125,0,0.35)]"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2 }}
      >
        {/* The Trinity Framework */}
        {/* The Trinity Impact! */}
        The Trinity Effect!
      </motion.h2>


      {/* --- 6. Framework Cards WITH IMAGE ICONS --- */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-12">
        {steps.map((step, idx) => {
          const IconComp = iconMap[step.title];

          return (
            <motion.div
              key={step.title}
              className="
                backdrop-blur-sm bg-white/5 border border-white/10
                p-10 rounded-xl shadow-[0_0_30px_rgba(255,125,0,0.15)]
                hover:shadow-[0_0_50px_rgba(255,125,0,0.25)]
                hover:bg-white/10 transition-all
                flex flex-col items-center text-center
              "
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 1.0,
                delay: idx * 0.25,
                ease: "easeOut",
              }}
            >

              {/* --- Icon Wrapper (Center) --- */}
              <div
                className="
                  mb-6 h-20 w-20 rounded-full flex items-center justify-center
                  bg-gradient-to-br from-orange-600/25 to-black/20
                  border border-orange-500/20
                  shadow-[0_0_25px_rgba(255,125,0,0.35)]
                "
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 + idx * 0.2 }}
                >
                  <IconComp className="h-12 w-12 text-orange-400 drop-shadow-[0_0_10px_rgba(255,125,0,0.6)]" />
                </motion.div>
              </div>

              {/* --- Title --- */}
              <h3 className="text-2xl font-semibold text-orange-400 mb-4">
                {step.title}
              </h3>

              {/* --- Description --- */}
              <div className="space-y-3">
                {step.lines.map((line, i) => (
                  <p
                    key={i}
                    className="text-gray-300 leading-relaxed text-base font-extralight"
                  >
                    {line}
                  </p>
                ))}
              </div>

              {/* <p className="text-gray-300 leading-relaxed text-base font-extralight">
                {step.desc}
              </p> */}

            </motion.div>
          );
        })}
      </div>


      {/* --- Motion Keyframes --- */}
      <style>{`
        @keyframes trinityRing1 {
          0% { transform: scale(0.97); opacity: 0.3; }
          50% { transform: scale(1.03); opacity: 0.45; }
          100% { transform: scale(0.97); opacity: 0.3; }
        }
        .animate-trinity-ring-1 {
          animation: trinityRing1 12s ease-in-out infinite;
        }

        @keyframes trinityRing2 {
          0% { transform: scale(1); opacity: 0.25; }
          50% { transform: scale(1.04); opacity: 0.4; }
          100% { transform: scale(1); opacity: 0.25; }
        }
        .animate-trinity-ring-2 {
          animation: trinityRing2 16s ease-in-out infinite;
        }

        @keyframes trinityRing3 {
          0% { transform: scale(1.03); opacity: 0.2; }
          50% { transform: scale(0.96); opacity: 0.35; }
          100% { transform: scale(1.03); opacity: 0.2; }
        }
        .animate-trinity-ring-3 {
          animation: trinityRing3 18s ease-in-out infinite;
        }

        @keyframes frameworkSpark {
          0% { transform: translateY(0) scale(0.7); opacity: 0.15; }
          50% { transform: translateY(-35px) scale(1); opacity: 0.6; }
          100% { transform: translateY(0) scale(0.7); opacity: 0.15; }
        }
        .animate-framework-spark {
          animation: frameworkSpark linear infinite;
        }
      `}</style>

    </section>

    // <section className="relative overflow-hidden py-36 bg-black text-white">

    //   {/* --- 1. Deep Gradient Background --- */}
    //   <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-900/10 to-black"></div>

    //   {/* --- 2. Central Axis Glow --- */}
    //   <div
    //     className="
    //       absolute left-1/2 top-0 -translate-x-1/2
    //       w-[2px] h-full
    //       bg-gradient-to-b from-orange-600/40 via-orange-500/15 to-transparent
    //       blur-[2px]
    //     "
    //   ></div>

    //   {/* --- 3. Tri-Arc Rings (Signature Trinity Motif) --- */}
    //   <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
    //     <div className="absolute w-[900px] h-[900px] rounded-full border border-orange-500/10 blur-[2px] animate-trinity-ring-1"></div>
    //     <div className="absolute w-[700px] h-[700px] rounded-full border border-orange-400/10 blur-[2px] animate-trinity-ring-2"></div>
    //     <div className="absolute w-[500px] h-[500px] rounded-full border border-orange-300/10 blur-[2px] animate-trinity-ring-3"></div>
    //   </div>

    //   {/* --- 4. Floating Particles (intense but clean) --- */}
    //   <div className="pointer-events-none absolute inset-0">
    //     {[...Array(250)].map((_, i) => (
    //       <div
    //         key={i}
    //         className="absolute w-[3px] h-[3px] bg-orange-400/30 rounded-full animate-framework-spark"
    //         style={{
    //           top: `${Math.random() * 100}%`,
    //           left: `${Math.random() * 100}%`,
    //           animationDelay: `${Math.random() * 3}s`,
    //           animationDuration: `${3 + Math.random() * 3}s`,
    //         }}
    //       ></div>
    //     ))}
    //   </div>

    //   {/* --- 5. Section Heading --- */}
    //   <motion.h2
    //     className="relative z-20 text-center text-4xl md:text-5xl font-bold text-orange-400 mb-16 drop-shadow-[0_0_20px_rgba(255,125,0,0.35)]"
    //     initial={{ opacity: 0, y: 30 }}
    //     whileInView={{ opacity: 1, y: 0 }}
    //     viewport={{ once: true }}
    //     transition={{ duration: 1.2 }}
    //   >
    //     The Trinity Framework
    //   </motion.h2>

    //   {/* --- 6. Framework Cards: Induce | Imprint | Illuminate --- */}
    //   <div className="relative z-20 max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-12">
    //     {steps.map((step, idx) => (
    //       <motion.div
    //         key={step.title}
    //         className="
    //           backdrop-blur-sm bg-white/5 border border-white/10
    //           p-10 rounded-xl shadow-[0_0_30px_rgba(255,125,0,0.15)]
    //           hover:shadow-[0_0_50px_rgba(255,125,0,0.25)]
    //           hover:bg-white/10 transition-all
    //         "
    //         initial={{ opacity: 0, y: 50 }}
    //         whileInView={{ opacity: 1, y: 0 }}
    //         viewport={{ once: true }}
    //         transition={{
    //           duration: 1.0,
    //           delay: idx * 0.25,
    //           ease: "easeOut",
    //         }}
    //       >
    //         <h3 className="text-2xl font-semibold text-orange-400 mb-4">
    //           {step.title}
    //         </h3>
    //         <p className="text-gray-300 leading-relaxed text-base font-extralight">
    //           {step.desc}
    //         </p>
    //       </motion.div>
    //     ))}
    //   </div>

    //   {/* --- Motion Keyframes --- */}
    //   <style>{`
    //     @keyframes trinityRing1 {
    //       0% { transform: scale(0.97); opacity: 0.3; }
    //       50% { transform: scale(1.03); opacity: 0.45; }
    //       100% { transform: scale(0.97); opacity: 0.3; }
    //     }
    //     .animate-trinity-ring-1 {
    //       animation: trinityRing1 12s ease-in-out infinite;
    //     }

    //     @keyframes trinityRing2 {
    //       0% { transform: scale(1); opacity: 0.25; }
    //       50% { transform: scale(1.04); opacity: 0.4; }
    //       100% { transform: scale(1); opacity: 0.25; }
    //     }
    //     .animate-trinity-ring-2 {
    //       animation: trinityRing2 16s ease-in-out infinite;
    //     }

    //     @keyframes trinityRing3 {
    //       0% { transform: scale(1.03); opacity: 0.2; }
    //       50% { transform: scale(0.96); opacity: 0.35; }
    //       100% { transform: scale(1.03); opacity: 0.2; }
    //     }
    //     .animate-trinity-ring-3 {
    //       animation: trinityRing3 18s ease-in-out infinite;
    //     }

    //     @keyframes frameworkSpark {
    //       0% { transform: translateY(0) scale(0.7); opacity: 0.15; }
    //       50% { transform: translateY(-35px) scale(1); opacity: 0.6; }
    //       100% { transform: translateY(0) scale(0.7); opacity: 0.15; }
    //     }
    //     .animate-framework-spark {
    //       animation: frameworkSpark linear infinite;
    //     }
    //   `}</style>
    // </section>
    // <section className="py-24 bg-gradient-to-b from-black via-orange-800 to-black text-white text-center">
    //   <div className="max-w-6xl mx-auto px-4">

    //     <motion.h2
    //       className="text-3xl font-bold text-orange-300 mb-12"
    //       initial={{ opacity: 0, y: -20 }}
    //       whileInView={{ opacity: 1, y: 0 }}
    //       viewport={{ once: true }}
    //     >
    //       The Trinity Framework
    //     </motion.h2>

    //     <div className="grid md:grid-cols-3 gap-10">
    //       {steps.map((s, i) => (
    //         <motion.div
    //           key={s.title}
    //           className="p-8 bg-white/5 rounded-xl border border-white/10 shadow-md"
    //           initial={{ opacity: 0, y: 20 }}
    //           whileInView={{ opacity: 1, y: 0 }}
    //           viewport={{ once: true }}
    //           transition={{ delay: i * 0.2 }}
    //         >
    //           <h3 className="text-2xl font-semibold text-orange-400 mb-3">
    //             {s.title}
    //           </h3>
    //           <p className="text-gray-300">{s.desc}</p>
    //         </motion.div>
    //       ))}
    //     </div>
    //   </div>
    // </section>
  );
}